/**
 * Auth Route Component
 *
 * Route component that redirects authenticated users away from auth pages.
 * Used for login, register, forgot password, and reset password pages.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import type { AuthRouteProps } from './AuthRoute.types';

import { useAuth } from '@/hooks/auth';
import { PROTECTED_ROUTES } from '@/utils';

/**
 * Auth route component that redirects authenticated users
 */
export const AuthRoute: React.FC<AuthRouteProps> = ({
  children,
  fallback,
  redirectTo = PROTECTED_ROUTES.DASHBOARD,
  requireGuest = true,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      fallback || (
        <div className="min-h-screen bg-contrast-950 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stroke-strong mx-auto" />
              <p className="mt-4 text-label-sm text-neutral-400">
                Checking authentication...
              </p>
            </div>
          </div>
        </div>
      )
    );
  }

  // If guest access is required but user is authenticated, redirect to dashboard
  if (requireGuest && isAuthenticated) {
    // Get return URL from location state or use default
    const returnUrl = location.state?.from?.pathname || redirectTo;

    return <Navigate to={returnUrl} replace />;
  }

  // If guest access is not required or user is not authenticated, render children
  return <>{children}</>;
};

export default AuthRoute;
